import { useInboxItems, useUpdateInboxItem } from "@/api/inbox";
import { InboxGroupHeader } from "@/components/InboxGroupHeader/InboxGroupHeader";
import { useAppNavigate, usePaneLocation } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { useInboxItemSelection } from "@/hooks/useInboxItemSelection";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { InboxItem } from "@cohesive/api-client";
import { InboxCard, Spinner } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "framer-motion";
import { Inbox, Zap } from "lucide-react";
import React, { useMemo, useState } from "react";

interface HomeActivityFeedProps {
  className?: string;
}

// Helper functions

const getMostRecentDate = (item: InboxItem): Date => {
  const dates: Date[] = [];

  if (item.flow_session) {
    if (item.flow_session.created_at) {
      dates.push(new Date(item.flow_session.created_at));
    }
    if (item.flow_session.updated_at) {
      dates.push(new Date(item.flow_session.updated_at));
    }
  }

  if (dates.length === 0) {
    dates.push(new Date(item.created_at));
    dates.push(new Date(item.updated_at));
  }

  return new Date(Math.max(...dates.map((date) => date.getTime())));
};

export const HomeActivityFeed: React.FC<HomeActivityFeedProps> = ({
  className,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());

  const { mutate: updateInboxItem } = useUpdateInboxItem();
  const appNavigate = useAppNavigate();
  const rightPaneLocation = usePaneLocation("rightPane");

  // Fetch items with pending tool confirmations (Needs Action)
  const { data: needsActionItems, isLoading: isNeedsActionLoading } =
    useInboxItems(undefined, {
      is_archived: false,
      has_pending_tool_confirmations: true,
    });

  // Fetch items without pending tool confirmations (Inbox Items)
  const { data: recentItems, isLoading: isRecentLoading } = useInboxItems(
    undefined,
    {
      is_archived: false,
      has_pending_tool_confirmations: false,
    },
  );

  // Process items by filtering out initialized sessions and sorting
  const processedNeedsActionItems = useMemo(() => {
    if (!needsActionItems) return [];
    // Filter out archived items
    const unarchivedItems = needsActionItems.filter(
      (item) => !item.is_archived,
    );

    // Sort chronologically (most recent first)
    return unarchivedItems.sort(
      (a, b) => getMostRecentDate(b).getTime() - getMostRecentDate(a).getTime(),
    );
  }, [needsActionItems]);

  const processedRecentItems = useMemo(() => {
    if (!recentItems) return [];

    // Filter out archived items
    const unarchivedItems = recentItems.filter((item) => !item.is_archived);

    // Sort chronologically (most recent first)
    return unarchivedItems.sort(
      (a, b) => getMostRecentDate(b).getTime() - getMostRecentDate(a).getTime(),
    );
  }, [recentItems]);

  // Combine all items for selection hook compatibility
  const allItems = useMemo(() => {
    return [...processedNeedsActionItems, ...processedRecentItems];
  }, [processedNeedsActionItems, processedRecentItems]);

  // Use selection hook
  const selection = useInboxItemSelection({ items: allItems });

  // Determine loading states
  const isLoading = isNeedsActionLoading || isRecentLoading;

  // Group items into sections (filtering out completed items for needs action section)
  const { awaitingReviewItems, allOtherItems } = useMemo(() => {
    const needsAction = processedNeedsActionItems.filter(
      (item) => !completedItems.has(item.id),
    );
    const other = processedRecentItems;

    return {
      awaitingReviewItems: needsAction,
      allOtherItems: other,
    };
  }, [processedNeedsActionItems, processedRecentItems, completedItems]);

  // Helper to check if item is currently viewed
  const isCurrentlyViewed = (item: InboxItem): boolean => {
    if (!rightPaneLocation?.pathname) return false;

    const pathname = rightPaneLocation.pathname;
    const inboxItemMatch = pathname.match(/\/inbox\/([^\/]+)/);
    if (inboxItemMatch && inboxItemMatch[1] === item.id) return true;

    const sessionMatch = pathname.match(/\/sessions\/([^\/]+)/);
    if (sessionMatch && sessionMatch[1] === item.flow_session?.id) return true;

    return false;
  };

  // Event handlers
  const handleItemClick = (item: InboxItem) => {
    // Mark as read if unread
    if (!item.is_read) {
      updateInboxItem({
        id: item.id,
        data: { is_read: true },
      });
    }

    appNavigate({
      pane: "rightPane",
      to: route(ROUTES.INBOX.ITEM, {
        inboxItemId: item.id,
      }),
    });

    analytics.trackInboxItemClicked({ inboxItem: item });
  };

  const handleStarClick = (e: React.MouseEvent, item: InboxItem) => {
    e.stopPropagation();
    analytics.trackInboxItemStarred({ inboxItem: item });
    updateInboxItem({
      id: item.id,
      data: { is_starred: !item.is_starred },
    });
  };

  const handleArchiveClick = (e: React.MouseEvent, item: InboxItem) => {
    e.stopPropagation();
    analytics.trackInboxItemArchived({ inboxItem: item });
    updateInboxItem({
      id: item.id,
      data: { is_archived: !item.is_archived },
    });
  };

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleDismissCompleted = (itemId: string) => {
    setCompletedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    const item = allItems.find((i) => i.id === itemId);
    if (item && selection.isItemSelected(itemId)) {
      selection.handleSelectionChange(false, item);
    }
  };

  // Render inbox card
  const renderInboxCard = (item: InboxItem, index: number) => (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
    >
      <InboxCard
        item={item}
        onClick={() => handleItemClick(item)}
        onStarClick={(e) => handleStarClick(e, item)}
        onArchiveClick={(e) => handleArchiveClick(e, item)}
        onSelectionChange={selection.handleSelectionChange}
        isSelected={selection.isItemSelected(item.id)}
        isCurrentlyViewed={isCurrentlyViewed(item)}
        completedItems={completedItems}
        recentlyCompletedItems={new Set()} // Simplified - no animation tracking
        onDismissCompleted={handleDismissCompleted}
        showDismissButton={completedItems.has(item.id)}
      />
    </motion.div>
  );

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-20", className)}>
        <Spinner size="xl" />
      </div>
    );
  }

  // Empty state - return null for blank display
  if (awaitingReviewItems.length === 0 && allOtherItems.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-3 flex flex-col overflow-hidden", className)}>
      <div className="w-full sm:max-w-[776px] mx-auto flex flex-col flex-1 min-h-0">
        {/* Scrollable Content */}
        <div className="flex-1 flex flex-col mt-4 min-h-0">
          <div className="flex-1 overflow-y-auto scrollbar-hide pb-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {/* Needs Action Section */}
              {awaitingReviewItems.length > 0 && (
                <div className="space-y-2">
                  <InboxGroupHeader
                    groupId="needs-action"
                    groupName="Needs Action"
                    items={awaitingReviewItems}
                    icon={
                      <Zap className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                    }
                    badgeCount={awaitingReviewItems.length}
                    isCollapsed={collapsedGroups.has("needs-action")}
                    onToggleGroup={toggleGroup}
                    onToggleGroupSelection={selection.toggleGroupSelection}
                    areAllGroupItemsSelected={
                      selection.areAllGroupItemsSelected
                    }
                    selectedCount={selection.selectedCount}
                    onBulkStar={selection.handleBulkStar}
                    onBulkArchive={selection.handleBulkArchive}
                    onClearSelection={selection.handleClearSelection}
                    selectedItemsStarred={selection.selectedItemsStarred}
                    selectedItemsArchived={selection.selectedItemsArchived}
                  />

                  <AnimatePresence initial={false}>
                    {!collapsedGroups.has("needs-action") && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 px-1">
                          {awaitingReviewItems.map((item, index) =>
                            renderInboxCard(item, index),
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              {/* All Items Section */}
              {allOtherItems.length > 0 && (
                <div className="space-y-2">
                  <InboxGroupHeader
                    groupId="all-items"
                    groupName="Inbox Items"
                    items={allOtherItems}
                    icon={
                      <Inbox className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                    }
                    isCollapsed={collapsedGroups.has("all-items")}
                    badgeCount={0}
                    onToggleGroup={toggleGroup}
                    onToggleGroupSelection={selection.toggleGroupSelection}
                    areAllGroupItemsSelected={
                      selection.areAllGroupItemsSelected
                    }
                    selectedCount={
                      awaitingReviewItems.length > 0
                        ? 0
                        : selection.selectedCount
                    }
                    onBulkStar={
                      awaitingReviewItems.length > 0
                        ? undefined
                        : selection.handleBulkStar
                    }
                    onBulkArchive={
                      awaitingReviewItems.length > 0
                        ? undefined
                        : selection.handleBulkArchive
                    }
                    onClearSelection={
                      awaitingReviewItems.length > 0
                        ? undefined
                        : selection.handleClearSelection
                    }
                  />

                  <AnimatePresence initial={false}>
                    {!collapsedGroups.has("all-items") && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 px-1">
                          {allOtherItems.map((item, index) =>
                            renderInboxCard(item, index),
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
